<template>
  <div class="h-screen bg-gray-50 pt-5 pb-1">
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2.5 text-center">
        Notification Preferences
      </h1>

      <div
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 flex-1 flex flex-col"
      >
        <UiSingleSelect
          v-model="userSelectedRole"
          title="ROLE"
          label="title"
          :options="userRoleOptions"
          rules="required"
          :disabled="!!queryRole"
        />
        <div
          v-if="isLoading && !notificationPreferencesList.length"
          class="flex justify-center items-center py-16"
        >
          <Loader :content="true" />
        </div>
        <div v-else class="flex flex-col h-full">
          <div class="flex-1">
            <TableWrapper>
              <THead>
                <TRHead>
                  <TH v-for="(head, index) in visibleTableHeads" :key="index">
                    {{ head }}
                  </TH>
                </TRHead>
              </THead>
              <TBody>
                <TRBody v-for="(preference, index) in notificationPreferencesList" :key="index">
                  <TD v-i18n="leftBar">{{ preference?.feature_name }}</TD>
                  <TD>
                    <ToggleButton
                      v-model="preference.notifications_enabled"
                      class="mt-1"
                      :is-status-hide="true"
                      :disabled="isLoading"
                      @change="toggleNotificationPreference(preference.feature_name, index)"
                    />
                  </TD>
                  <TD v-if="hasReportAccess">
                    <template v-if="!isEmpty(preference?.reports)">
                      <ToggleButton
                        v-model="preference.reports[getReportsKey(preference.reports, 'MONTHLY')]"
                        class="mt-1"
                        :is-status-hide="true"
                        @change="
                          toggleNotificationPreference(
                            preference.feature_name,
                            index,
                            getReportsKey(preference.reports, 'MONTHLY'),
                          )
                        "
                      />
                    </template>
                    <template v-else>
                      {{ GENERAL_CONSTANTS.NOT_APPLICABLE }}
                    </template>
                  </TD>
                  <TD v-if="hasReportAccess">
                    <template v-if="!isEmpty(preference?.reports)">
                      <ToggleButton
                        v-model="preference.reports[getReportsKey(preference.reports, 'WEEKLY')]"
                        class="mt-1"
                        :is-status-hide="true"
                        @change="
                          toggleNotificationPreference(
                            preference.feature_name,
                            index,
                            getReportsKey(preference.reports, 'WEEKLY'),
                          )
                        "
                      />
                    </template>
                    <template v-else>
                      {{ GENERAL_CONSTANTS.NOT_APPLICABLE }}
                    </template>
                  </TD>
                </TRBody>
              </TBody>
            </TableWrapper>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import {
  TableWrapper,
  THead,
  TRHead,
  TH,
  TBody,
  TRBody,
  TD,
} from '@src/components/UiElements/TableElements/index.js'
import ToggleButton from '@src/components/BaseComponent/toggleButton.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { objectDeepCopy } from '@/src/utils/generalUtil'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import isEmpty from 'lodash/isEmpty'
export default {
  name: 'PublicNotificationPreferencesView',
  components: {
    TableWrapper,
    THead,
    TRHead,
    TH,
    TBody,
    TRBody,
    TD,
    ToggleButton,
    Loader,
    UiSingleSelect,
  },
  data() {
    return {
      GENERAL_CONSTANTS,
      userSelectedRole: '',
      sub_domain: this.getHostName(),
      userPreferencesObject: {},
      leftBar: 'leftBar',
      notificationPreferencesList: [],
      tableHeads: ['Feature Name', 'Notification Status', 'Monthly Report', 'Weekly Report'],
      isLoading: false,
    }
  },
  computed: {
    userRoleOptions() {
      const roles = this.userPreferencesObject?.roles
      return roles
    },
    token() {
      return this.$route.query.token
    },
    queryRole() {
      return this.$route.query.role
    },
    hasReportAccess() {
      return [TENANT_ROLES.SUPER_ADMIN, TENANT_ROLES.CAMPUS_ADMIN].includes(this.userSelectedRole)
    },
    visibleTableHeads() {
      if (this.hasReportAccess) {
        return this.tableHeads
      }
      return this.tableHeads.slice(0, 2)
    },
  },
  watch: {
    userSelectedRole: {
      async handler() {
        await this.fetchAllPreferences()
      },
    },
  },

  async mounted() {
    await this.fetchAllPreferences()
  },
  methods: {
    isEmpty,
    async fetchAllPreferences() {
      try {
        this.isLoading = true

        const payload = {
          token: this.token,
          sub_domain: this.sub_domain,
          role: this.queryRole || this.userSelectedRole,
        }
        const { data = {} } = await this.getPublicUserNotificationPreferences(payload)
        this.userPreferencesObject = objectDeepCopy(data || {})
        this.userSelectedRole = this.queryRole || data?.selected_role
        if (this.userPreferencesObject?.preferences?.length) {
          this.notificationPreferencesList = this.userPreferencesObject?.preferences
        }
      } finally {
        this.isLoading = false
      }
    },
    getReportsKey(reports, keyType = 'MONTHLY') {
      if (!reports || typeof reports !== 'object') return null

      const reportKey = Object.keys(reports).find((report) => report.includes(keyType))
      return reportKey || null
    },

    getHostName() {
      return window.location.hostname.split('.')[0]
    },

    async toggleNotificationPreference(preference, index, reportAction = null) {
      const payload = {
        token: this.token,
        sub_domain: this.sub_domain,
        role: this.userSelectedRole,
        feature_name: preference,
      }
      if (reportAction) {
        payload.report_name = reportAction
      }

      try {
        this.isLoading = true
        await this.updatePublicUserNotificationPreferences(payload)
      } finally {
        this.isLoading = false
      }
    },
    ...mapActions('notifications', [
      'getPublicUserNotificationPreferences',
      'updatePublicUserNotificationPreferences',
    ]),
  },
}
</script>
